import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import SubscriptionService from "../../services/SubscriptionService";
import {
  SaPageShell,
  SaPageHeader,
  SaCard,
  SaPrimaryButton,
  SaGhostButton,
  SaField,
  SaSectionTitle,
  saInputSx,
  SA,
} from "../shared/SuperAdminUi";

function toLocalDateTimeValue(v) {
  if (v == null || v === "") return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return typeof v === "string" ? v.slice(0, 16) : "";
  }
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditSubscription() {
  const { id } = useParams();
  const toast = useRef(null);
  const navigate = useNavigate();
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    subscriptionPlanId: "",
    price: "",
    startDate: "",
    endDate: "",
    isActive: false,
    isPaid: false,
    id,
    clientId: "",
    createdBy: "",
    modifiedBy: "",
    createdOn: "",
    modifiedOn: "",
  });
  const [formError, setFormError] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const subscription = await SubscriptionService.getSubscriptionById(id);
        if (cancelled || !subscription) return;
        setForm((prev) => ({
          ...prev,
          ...subscription,
          subscriptionPlanId:
            subscription.subscriptionPlanId ?? subscription.subscriptionPlan?.id ?? "",
          price: subscription.price ?? "",
          startDate: toLocalDateTimeValue(subscription.startDate),
          endDate: toLocalDateTimeValue(subscription.endDate),
          isActive: !!subscription.isActive,
          isPaid: !!subscription.isPaid,
          clientId: subscription.clientId ?? "",
          id: subscription.id ?? id,
        }));
      } catch (error) {
        console.error("Error fetching subscription:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load subscription.",
          life: 4000,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const plans = await SubscriptionService.getAllSubscriptionPlans();
        if (!cancelled) setSubscriptionPlans(Array.isArray(plans) ? plans : []);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "clientId") return;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setFormError((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!String(form.subscriptionPlanId ?? "").trim()) errors.subscriptionPlanId = "Required";
    if (!String(form.price ?? "").trim()) errors.price = "Required";
    if (!String(form.startDate ?? "").trim()) errors.startDate = "Required";
    if (!String(form.endDate ?? "").trim()) errors.endDate = "Required";
    if (!String(form.clientId ?? "").trim()) errors.clientId = "Missing client on this subscription.";
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const appendFormFields = (fd) => {
    const skip = new Set(["subscriptionPlan"]);
    Object.keys(form).forEach((key) => {
      if (skip.has(key)) return;
      const val = form[key];
      if (val === undefined || val === null) return;
      if (typeof val === "boolean") {
        fd.append(key, val ? "true" : "false");
      } else {
        fd.append(key, val);
      }
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation",
        detail: "Fill required fields.",
        life: 3000,
      });
      return;
    }

    const formDataToSend = new FormData();
    appendFormFields(formDataToSend);

    setSaving(true);
    try {
      await SubscriptionService.updateSubscription(id, formDataToSend);
      toast.current?.show({
        severity: "success",
        summary: "Saved",
        detail: "Subscription updated successfully.",
        life: 2800,
      });
      setTimeout(() => navigate(-1), 700);
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to update subscription.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "0 20px",
  };

  const err = (name) =>
    formError[name] ? (
      <p style={{ margin: "6px 0 0", fontSize: "12px", color: SA.danger }}>{formError[name]}</p>
    ) : null;

  return (
    <SaPageShell>
      <Toast ref={toast} />
      <SaPageHeader
        title="Edit subscription"
        subtitle={
          loading
            ? "Loading…"
            : `Subscription #${id} · Client ${form.clientId || "—"}`
        }
        actions={
          <SaGhostButton type="button" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left" style={{ marginRight: "8px" }} />
            Back
          </SaGhostButton>
        }
      />

      <form onSubmit={onSubmit}>
        <SaCard>
          <SaSectionTitle iconClass="fas fa-receipt">Plan &amp; billing</SaSectionTitle>
          <div style={grid}>
            <SaField label="Subscription plan *">
              <select
                name="subscriptionPlanId"
                value={form.subscriptionPlanId}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.subscriptionPlanId ? SA.danger : SA.border }}
              >
                <option value="">{loadingPlans ? "Loading…" : "Select plan"}</option>
                {!loadingPlans &&
                  subscriptionPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.subscriptionPlanName}
                    </option>
                  ))}
              </select>
              {err("subscriptionPlanId")}
            </SaField>
            <SaField label="Price *">
              <input
                type="number"
                step="0.01"
                name="price"
                value={form.price}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.price ? SA.danger : SA.border }}
              />
              {err("price")}
            </SaField>
            <SaField label="Start *">
              <input
                type="datetime-local"
                name="startDate"
                value={form.startDate}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.startDate ? SA.danger : SA.border }}
              />
              {err("startDate")}
            </SaField>
            <SaField label="End *">
              <input
                type="datetime-local"
                name="endDate"
                value={form.endDate}
                onChange={handleInputChange}
                style={{ ...saInputSx, borderColor: formError.endDate ? SA.danger : SA.border }}
              />
              {err("endDate")}
            </SaField>
            <SaField label="Client ID (read-only)">
              <input
                name="clientId"
                value={form.clientId}
                readOnly
                aria-readonly="true"
                style={{
                  ...saInputSx,
                  background: SA.bgSoft,
                  cursor: "not-allowed",
                  borderColor: formError.clientId ? SA.danger : SA.border,
                }}
              />
              {err("clientId")}
            </SaField>
          </div>

          <SaSectionTitle iconClass="fas fa-toggle-on">Status</SaSectionTitle>
          <SaField label="Active">
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
              <input
                type="checkbox"
                name="isActive"
                checked={!!form.isActive}
                onChange={handleInputChange}
                style={{ width: "18px", height: "18px" }}
              />
              Subscription is active
            </label>
          </SaField>
          <SaField label="Payment received">
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", fontSize: "14px", color: SA.ink }}>
              <input
                type="checkbox"
                name="isPaid"
                checked={!!form.isPaid}
                onChange={handleInputChange}
                style={{ width: "18px", height: "18px" }}
              />
              Mark as paid
            </label>
          </SaField>

          <div
            style={{
              marginTop: "28px",
              paddingTop: "22px",
              borderTop: `1px solid ${SA.border}`,
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <SaPrimaryButton type="submit" disabled={saving || loading} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-save"}>
              {saving ? "Saving…" : "Save changes"}
            </SaPrimaryButton>
            <SaGhostButton type="button" onClick={() => navigate(-1)}>
              Cancel
            </SaGhostButton>
          </div>
        </SaCard>
      </form>
    </SaPageShell>
  );
}

export default EditSubscription;
